import { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import {
  Modal as UIModal,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  VStack,
  Spinner
} from "@gluestack-ui/themed";
import { Icon } from "@repo/core/components/core/Icon";
import { Text } from "@repo/core/components/core/Text";
import { useDimensions } from "@repo/core/hooks/useDimensions";
import {
  DocumentPreviewBottomSheetProps,
  IDocumentPreviewBottomSheetRefProps
} from "./types";
import { PdfViewer } from "@repo/core/screens/Proposals/screens/ProposalDocumentPreview/components/PdfViewer";
import { ProposalDocument } from "../../sections/DocumentsView/types";

export const DocumentPreviewBottomSheet = forwardRef<
  IDocumentPreviewBottomSheetRefProps,
  DocumentPreviewBottomSheetProps
>(function DocumentPreviewBottomSheet({ onDismiss }, ref) {
  const { height } = useDimensions();
  const [isOpen, setIsOpen] = useState(false);
  const [document, setDocument] = useState<ProposalDocument | null>(null);
  const [shouldRenderPdf, setShouldRenderPdf] = useState(false);

  // Calculate modal height - 90% of screen height with max height
  const modalHeight =
    height && height > 0
      ? Math.floor(height * 0.9)
      : typeof window !== "undefined"
        ? Math.floor(window.innerHeight * 0.9)
        : 720; // Default fallback

  const contentHeight = modalHeight - 100;

  // Reset PDF rendering when document changes
  useEffect(() => {
    if (isOpen && document) {
      setShouldRenderPdf(false);
      // Start loading PDF after a short delay when modal opens
      setTimeout(() => {
        setShouldRenderPdf(true);
      }, 200);
    } else {
      setShouldRenderPdf(false);
    }
  }, [document, isOpen]);

  useImperativeHandle(
    ref,
    (): IDocumentPreviewBottomSheetRefProps => ({
      open: (nextDocument: ProposalDocument) => {
        setDocument(nextDocument);
        setIsOpen(true);
      },
      close: () => {
        setIsOpen(false);
        setShouldRenderPdf(false);
        setDocument(null);
        onDismiss?.();
      }
    }),
    [onDismiss]
  );

  const handleClose = () => {
    setIsOpen(false);
    setShouldRenderPdf(false);
    setDocument(null);
    onDismiss?.();
  };

  // Check if document is PDF
  const isPdf =
    document?.extension?.toLowerCase() === "pdf" ||
    document?.base64?.includes("JVBERi0") ||
    document?.base64?.startsWith("data:application/pdf");

  // Get image source
  const getImageSource = () => {
    if (!document?.base64 || isPdf) {
      return document?.thumbnail || "";
    }
    if (document.base64.startsWith("data:")) {
      return document.base64;
    }
    const contentType =
      document.extension?.toLowerCase() === "png" ? "image/png" : "image/jpeg";
    return `data:${contentType};base64,${document.base64}`;
  };

  const imageSource = getImageSource();

  return (
    <UIModal
      isOpen={isOpen}
      onClose={handleClose}
      overflow="hidden"
      focusable={false}
      useRNModal={false}
    >
      <ModalBackdrop />
      <ModalContent
        bg="$backgroundLight0"
        width="90%"
        maxWidth={1200}
        maxHeight={modalHeight}
        height={modalHeight}
        focusScope={false}
      >
        <ModalHeader justifyContent="space-between" alignItems="center" pb="$2">
          {document?.title ? (
            <Text
              fontSize="$md"
              fontWeight={600}
              color="$textLight900"
              flex={1}
            >
              {document.title}
            </Text>
          ) : (
            <VStack flex={1} />
          )}
          <ModalCloseButton>
            <Icon name="times" size="xs" />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody flex={1} style={{ height: `${contentHeight}px` }}>
          {document ? (
            <>
              {isPdf && document.base64 ? (
                shouldRenderPdf ? (
                  <div
                    style={{
                      width: "100%",
                      height: `${contentHeight}px`,
                      position: "relative"
                    }}
                  >
                    <PdfViewer
                      base64={document.base64}
                      documentTitle={document.title}
                    />
                  </div>
                ) : (
                  <VStack
                    flex={1}
                    alignItems="center"
                    justifyContent="center"
                    gap="$4"
                    style={{ height: `${contentHeight}px` }}
                  >
                    <Spinner color="$primary700" size="large" />
                    <Text
                      fontSize="$sm"
                      color="$textLight600"
                      textAlign="center"
                    >
                      Carregando PDF...
                    </Text>
                  </VStack>
                )
              ) : imageSource ? (
                <div
                  style={{
                    width: "100%",
                    height: `${contentHeight}px`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "auto",
                    padding: "20px",
                    backgroundColor: "#F3F4F6"
                  }}
                >
                  <img
                    src={imageSource}
                    alt={document.title || "Documento"}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      borderRadius: "8px",
                      display: "block"
                    }}
                  />
                </div>
              ) : (
                <VStack
                  flex={1}
                  alignItems="center"
                  justifyContent="center"
                  gap="$4"
                  style={{ height: `${contentHeight}px` }}
                >
                  <Spinner color="$primary700" size="large" />
                  <Text fontSize="$sm" color="$textLight600" textAlign="center">
                    Carregando documento...
                  </Text>
                </VStack>
              )}
            </>
          ) : (
            <VStack
              flex={1}
              alignItems="center"
              justifyContent="center"
              gap="$4"
              style={{ height: `${contentHeight}px` }}
            >
              <Text fontSize="$sm" color="$textLight600" textAlign="center">
                Nenhum documento selecionado
              </Text>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </UIModal>
  );
});
